"use client"

import Link from "next/link"
import { Users, Check, AlertTriangle, Sparkles, Lightbulb, ShieldAlert } from "lucide-react"
import { FamilyWarningVoteButton } from "@/components/media/FamilyWarningVoteButton"
import {
  FamilyFitQuickSetup,
  FamilyFitSignIn,
} from "@/components/media/FamilyFitConversionFlow"
import { MemberAvatar } from "@/components/ui/MemberAvatar"
import { cn } from "@/lib/utils"
import { APERCU_PALETTE } from "@/components/home-v2/apercuTheme"
import { FAMILY_FIT_LABELS, familyFitBandFromLevel, type FamilyFitBand } from "@/lib/family-fit-display"
import { useFamilyFitData } from "@/components/media/FicheDataContext"

const SAGE = "#5C8A5C"
const AMBER = "#C08A3E"

// ---------------------------------------------------------------------------
// Types (shared with FamilyFitCard)
// ---------------------------------------------------------------------------

interface FamilyFitHeroProps {
  mediaId: string
}

// ---------------------------------------------------------------------------
// Level configuration (dark-theme variants)
// ---------------------------------------------------------------------------

const BAND_CONFIG: Record<
  FamilyFitBand,
  { label: string; pillBg: string; pillText: string; icon: React.ComponentType<{ className?: string }> }
> = {
  veryAdapted: {
    label: FAMILY_FIT_LABELS.veryAdapted,
    pillBg: "rgba(92, 138, 92, 0.14)",
    pillText: SAGE,
    icon: Check,
  },
  goodChoice: {
    label: FAMILY_FIT_LABELS.goodChoice,
    pillBg: "rgba(62, 126, 156, 0.12)",
    pillText: "#3E7E9C",
    icon: Check,
  },
  check: {
    label: FAMILY_FIT_LABELS.check,
    pillBg: "rgba(192, 138, 62, 0.14)",
    pillText: AMBER,
    icon: AlertTriangle,
  },
  notYet: {
    label: FAMILY_FIT_LABELS.notYet,
    pillBg: "rgba(209, 106, 74, 0.14)",
    pillText: "#D16A4A",
    icon: ShieldAlert,
  },
}

// ---------------------------------------------------------------------------
// Skeleton (dark variant)
// ---------------------------------------------------------------------------

function HeroSkeleton() {
  const p = APERCU_PALETTE
  return (
    <div
      className="rounded-xl p-5"
      style={{ background: p.card, border: `1px solid ${p.line}` }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div
          className="h-5 w-5 rounded animate-pulse"
          style={{ background: p.bg2 }}
        />
        <div
          className="h-5 w-36 rounded animate-pulse"
          style={{ background: p.bg2 }}
        />
      </div>
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-full animate-pulse"
                style={{ background: p.bg2 }}
              />
              <div
                className="h-4 w-20 rounded animate-pulse"
                style={{ background: p.bg2 }}
              />
            </div>
            <div
              className="h-6 w-16 rounded-full animate-pulse"
              style={{ background: p.bg2 }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FamilyFitHero({ mediaId }: FamilyFitHeroProps) {
  // Reads the shared fiche data (single fetch via FicheDataProvider on the
  // media page); self-fetches when rendered without the provider.
  const { data, loading } = useFamilyFitData(mediaId)

  // ---------- Loading ----------
  if (loading) return <HeroSkeleton />

  const p = APERCU_PALETTE
  const serifClass = "font-serif"
  const shellStyle = {
    background: p.card,
    border: `1px solid ${p.line}`,
    boxShadow: "0 1px 2px rgba(58,46,34,.05), 0 14px 34px -18px rgba(58,46,34,.18)",
  }

  // ---------- Not logged in ----------
  if (data?.status === "not_logged_in") {
    return (
      <div className="rounded-2xl p-5" style={shellStyle}>
        <div className="flex items-center gap-2 mb-3">
          <div
            className="p-1.5 rounded-lg"
            style={{ background: p.bg2, color: p.accent }}
          >
            <Users className="h-4 w-4" />
          </div>
          <h3
            className={`${serifClass} text-base font-medium`}
            style={{ color: p.ink, letterSpacing: "-0.02em" }}
          >
            Adapté à votre famille ?
          </h3>
        </div>
        <p className="text-sm leading-relaxed mb-4" style={{ color: p.ink2 }}>
          L’âge ne suffit pas toujours. Obtenez un repère adapté aux sensibilités de votre foyer.
        </p>
        <FamilyFitSignIn variant="warm" />
      </div>
    )
  }

  // ---------- No family members ----------
  if (data?.status === "no_family") {
    return (
      <div className="rounded-2xl p-5" style={shellStyle}>
        <div className="flex items-center gap-2 mb-3">
          <div
            className="p-1.5 rounded-lg"
            style={{ background: p.bg2, color: p.accent }}
          >
            <Users className="h-4 w-4" />
          </div>
          <h3
            className={`${serifClass} text-base font-medium`}
            style={{ color: p.ink, letterSpacing: "-0.02em" }}
          >
            Adapté à votre famille ?
          </h3>
        </div>
        <p className="text-sm leading-relaxed mb-4" style={{ color: p.ink2 }}>
          Ajoutez un premier profil pour obtenir votre réponse personnalisée sans quitter cette fiche.
        </p>
        <FamilyFitQuickSetup mediaId={mediaId} variant="warm" />
      </div>
    )
  }

  // ---------- No data (fetch error) ----------
  if (!data || (data.status !== "ok" && data.status !== "family_warning")) return null

  // ---------- OK or Family Warning: show members ----------
  const { members } = data
  const isFamilyWarning = data.status === "family_warning"

  return (
    <div className="rounded-2xl p-5" style={shellStyle}>
      <h3
        className={`${serifClass} text-base font-medium flex items-center gap-2 mb-2`}
        style={{ color: p.ink, letterSpacing: "-0.02em" }}
      >
        {isFamilyWarning ? (
          <>
            <ShieldAlert className="h-5 w-5" style={{ color: p.accent }} />
            <span style={{ color: p.accent }}>Attention famille</span>
          </>
        ) : (
          <>
            <Users className="h-5 w-5" style={{ color: p.accent }} />
            Repères pour ma famille
          </>
        )}
      </h3>
      {isFamilyWarning && (
        <p className="text-sm mb-3" style={{ color: p.accent }}>
          Ce contenu contient des éléments sensibles pour les familles avec
          enfants.
        </p>
      )}

      {/* Community warning vote button */}
      <div className="mb-4">
        <FamilyWarningVoteButton mediaId={mediaId} />
      </div>

      <p className="text-xs mb-4 rounded-lg px-3 py-2" style={{ background: p.bg2, color: p.ink2 }}>
        Pas d&apos;accord avec un repère pour un enfant ? Complétez{" "}
        <Link href="/profil" className="underline font-medium" style={{ color: p.accent }}>
          son quiz de préférences
        </Link>{" "}
        (goûts et sensibilité) — l&apos;âge expert se vote juste au-dessus.
      </p>

      <div className="space-y-3">
        {members.map((member) => {
          const config = BAND_CONFIG[familyFitBandFromLevel(member.level)]
          const Icon = config.icon

          return (
            <div key={member.id} className="space-y-1">
              {/* Top row: avatar + name + age  |  score pill */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <MemberAvatar
                    avatarStyle={member.avatarStyle ?? null}
                    avatarSeed={member.avatarSeed ?? null}
                    avatarOptions={member.avatarOptions ?? null}
                    avatarEmoji={member.avatarEmoji ?? null}
                    name={member.name}
                    size={20}
                  />
                  <span
                    className="font-medium text-sm truncate"
                    style={{ color: p.ink }}
                  >
                    {member.name}
                  </span>
                  {member.age != null && (
                    <span
                      className="text-xs flex-shrink-0"
                      style={{ color: p.ink2 }}
                    >
                      {member.age} ans
                    </span>
                  )}
                </div>

                {/* Score pill */}
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0"
                  )}
                  style={{
                    background: config.pillBg,
                    color: config.pillText,
                  }}
                  title={
                    member.hasPreferences === false
                      ? "Repère limité : faites le quiz famille pour affiner cette recommandation."
                      : undefined
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {config.label}
                </span>
              </div>

              {/* Reason */}
              <p className="text-xs pl-7" style={{ color: p.ink2 }}>
                {member.reason}
              </p>

              {/* Affinity insight */}
              {member.affinity?.affinityReason && (
                <p
                  className="text-xs pl-7 flex items-center gap-1"
                  style={{ color: p.accent2 }}
                >
                  <Lightbulb className="h-3 w-3 flex-shrink-0" />
                  {member.affinity.affinityReason}
                </p>
              )}

              {/* Quiz prompt for members without preferences */}
              {member.hasPreferences === false && (
                <Link
                  href={`/profil/quiz/${member.id}`}
                  className="text-xs pl-7 flex items-center gap-1 hover:opacity-70 transition-opacity"
                  style={{ color: p.accent }}
                >
                  <Sparkles className="h-3 w-3 flex-shrink-0" />
                  Faire le quiz pour affiner cet indice
                </Link>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
